import { useState, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@design-system/dialog';
import {
  assistantScope,
  useAssistantScope,
} from '../hooks/use-assistant-scope';
import { assistantClient } from '../services/assistant-client';

interface SourcePassage {
  title: string;
  source: string;
  content: string;
  revision: number;
  is_current: boolean;
  provenance: { pages?: number[]; sections?: string[] };
}

export function SourceCitation({
  id,
  children,
}: {
  id: number;
  children: ReactNode;
}) {
  const scope = useAssistantScope();
  return (
    <ScopedCitation
      key={`${scope}:${id}`}
      id={id}
      scope={scope}
    >
      {children}
    </ScopedCitation>
  );
}

function ScopedCitation({
  id,
  scope,
  children,
}: {
  id: number;
  scope: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<SourcePassage>();
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const path = `assistant/knowledge-citations/${id}/`;
  async function show() {
    setOpen(true);
    setError('');
    setSource(undefined);
    try {
      const response = await assistantClient.get<SourcePassage>(path);
      if (assistantScope() === scope) setSource(response.data);
    } catch {
      if (assistantScope() === scope)
        setError('This source is unavailable or you no longer have access.');
    }
  }
  async function download() {
    setDownloading(true);
    try {
      const response = await assistantClient.get<Blob>(path, {
        params: { download: 'true' },
        responseType: 'blob',
      });
      if (assistantScope() !== scope) return;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = source?.source || 'source';
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      if (assistantScope() === scope)
        setError('The source file could not be downloaded.');
    } finally {
      setDownloading(false);
    }
  }
  return (
    <>
      <button
        type='button'
        className='text-primary-1000 underline underline-offset-2'
        onClick={() => void show()}
      >
        {children}
      </button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
      >
        <DialogContent>
          <DialogTitle>
            {source?.title || source?.source || 'Knowledge source'}
          </DialogTitle>
          <DialogDescription>
            {source
              ? `${source.source}${source.provenance.pages?.length ? ` · Pages ${source.provenance.pages.join(', ')}` : ''}`
              : 'Retrieving the cited passage'}
          </DialogDescription>
          {error && <p role='alert'>{error}</p>}
          {!source && !error && <p role='status'>Loading source…</p>}
          {source && (
            <>
              {!source.is_current && (
                <p className='text-sm text-amber-700'>
                  This answer cites an earlier version of the document.
                </p>
              )}
              {!!source.provenance.sections?.length && (
                <p className='text-sm'>
                  {source.provenance.sections.join(' · ')}
                </p>
              )}
              <pre className='max-h-80 overflow-y-auto whitespace-pre-wrap rounded bg-gray-50 p-3 text-sm'>
                {source.content}
              </pre>
              <button
                type='button'
                disabled={downloading}
                className='text-primary-1000 underline'
                onClick={() => void download()}
              >
                {downloading ? 'Downloading…' : 'Download cited source'}
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
